import { popup } from '@tma.js/sdk-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  type ApiDocumentRequest,
  createAgencyOrder,
  fetchDocumentRequests,
} from '@/fetcher'
import { formatDate, formatRub } from '@/helpers/format'
import { useNav } from '@/nav'
import { TelegramBackButton } from './TelegramBackButton'
import { TelegramMainButton } from './TelegramMainButton'

const getToday = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())
  const values = Object.fromEntries(
    parts.map(({ type, value }) => [type, value]),
  )
  return `${values.year}-${values.month}-${values.day}`
}

export const AgencyOrderPage = () => {
  const { navigate } = useNav()
  const [requests, setRequests] = useState<ApiDocumentRequest[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [step, setStep] = useState<1 | 2>(1)
  const [amountRub, setAmountRub] = useState('')
  const [orderNo, setOrderNo] = useState('')
  const [documentDate, setDocumentDate] = useState(getToday)

  const loadRequests = useCallback(async () => {
    setIsLoading(true)
    setErrorMessage(null)
    try {
      const { requests: rows } = await fetchDocumentRequests({
        status: 'bank_received',
        agencyOrderNo: 'null',
        limit: 100,
      })
      setRequests(rows)
      setSelected((prev) => {
        const ids = new Set(rows.map((r) => r.id))
        const next = new Set<string>()
        for (const id of prev) {
          if (ids.has(id)) next.add(id)
        }
        return next
      })
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Не удалось загрузить заявки',
      )
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadRequests()
  }, [loadRequests])

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const allSelected = requests.length > 0 && selected.size === requests.length

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set())
    } else {
      setSelected(new Set(requests.map((r) => r.id)))
    }
  }

  const totalRub = useMemo(() => {
    let sum = 0
    for (const request of requests) {
      if (selected.has(request.id)) {
        sum += Number(request.amountRub)
      }
    }
    return sum
  }, [requests, selected])

  const handleConfirm = () => {
    if (selected.size === 0 || isSubmitting) return

    const parsedAmount = Number(amountRub.replace(',', '.'))
    const parsedOrderNo = Number(orderNo)
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setErrorMessage('Введите корректную сумму поручения')
      return
    }
    if (!Number.isInteger(parsedOrderNo) || parsedOrderNo <= 0) {
      setErrorMessage('Введите корректный номер поручения')
      return
    }

    const result = popup.show.ifAvailable({
      title: 'Формирование поручения',
      message: `Сформировать поручение №${parsedOrderNo} из ${selected.size} заяв(ки/ок) на сумму ${formatRub(parsedAmount)} ₽?`,
      buttons: [
        { id: 'confirm', type: 'default', text: 'Сформировать' },
        { id: 'cancel', type: 'cancel' },
      ],
    })

    if (!result.ok) return

    setIsSubmitting(true)
    result.data
      .then((buttonId?: string) => {
        if (buttonId !== 'confirm') return null
        return createAgencyOrder({
          requestIds: [...selected],
          orderNo: parsedOrderNo,
          amountRub: parsedAmount,
          documentDate,
        })
      })
      .then((res) => {
        if (!res) return
        setErrorMessage(null)
        navigate('settings')
      })
      .catch((error) => {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Не удалось сформировать поручение',
        )
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const handleNext = () => {
    if (selected.size === 0) return
    setAmountRub(String(totalRub))
    setErrorMessage(null)
    setStep(2)
  }

  return (
    <div className="py-8 mx-auto max-w-2xl px-4 pb-40">
      {step === 2 && (
        <TelegramBackButton isVisible onClick={() => setStep(1)} />
      )}

      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-[var(--app-text)]">
          Поручение по агентскому договору
        </h1>
      </div>

      {errorMessage && (
        <div className="mb-4 rounded-[18px] bg-[var(--app-danger-soft)] px-4 py-3 text-sm text-[var(--app-text)]">
          {errorMessage}
        </div>
      )}

      {step === 1 && !isLoading && requests.length === 0 && (
        <div className="rounded-[18px] bg-[var(--app-input)] px-4 py-6 text-center text-sm text-[var(--app-muted)]">
          Нет заявок, ожидающих добавления в поручение
        </div>
      )}

      {step === 1 && (
        <div className="space-y-2">
          {requests.map((request) => {
            const isSelected = selected.has(request.id)
            return (
              <button
                key={request.id}
                type="button"
                onClick={() => toggle(request.id)}
                className={`w-full rounded-[18px] border px-4 py-3 text-left transition ${
                  isSelected
                    ? 'border-[var(--app-accent)] bg-[var(--app-accent-soft)]'
                    : 'border-transparent bg-[var(--app-input)]'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <p className="truncate text-sm font-semibold text-[var(--app-text)]">
                      {request.data.fullNameRu}
                    </p>
                    <p className="text-xs text-[var(--app-muted)]">
                      Инвойс: {request.invoiceNo ?? '—'}
                    </p>
                    <p className="text-xs text-[var(--app-muted)]">
                      Поступление: {formatDate(request.bankReceivedAt)}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold text-[var(--app-text)]">
                      {formatRub(request.amountRub)} ₽
                    </p>
                    <p className="mt-1 text-xs text-[var(--app-muted)]">
                      {request.teamName}
                    </p>
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <p className="text-sm font-semibold text-[var(--app-text)]">
            Выбранные заявки: {selected.size}
          </p>
          <label className="block space-y-2 text-sm font-medium text-[var(--app-text)]">
            Сумма поручения, ₽
            <input
              type="text"
              inputMode="decimal"
              value={amountRub}
              onChange={(event) => setAmountRub(event.target.value)}
              className="h-12 w-full rounded-[22px] bg-[var(--app-input)] px-4 py-3 text-sm font-normal outline-none focus:ring-4 focus:ring-[var(--app-accent-soft)]"
            />
          </label>
          <label className="block space-y-2 text-sm font-medium text-[var(--app-text)]">
            Номер поручения
            <input
              type="text"
              inputMode="numeric"
              value={orderNo}
              onChange={(event) => setOrderNo(event.target.value)}
              className="w-full rounded-[22px] bg-[var(--app-input)] px-4 py-3 text-sm font-normal outline-none focus:ring-4 focus:ring-[var(--app-accent-soft)]"
            />
          </label>
          <label className="block space-y-2 text-sm font-medium text-[var(--app-text)]">
            Дата документа
            <input
              type="text"
              placeholder="ГГГГ-ММ-ДД"
              value={documentDate}
              onChange={(event) => setDocumentDate(event.target.value)}
              className="w-full rounded-[22px] bg-[var(--app-input)] px-4 py-3 text-sm font-normal outline-none focus:ring-4 focus:ring-[var(--app-accent-soft)]"
            />
          </label>
        </div>
      )}

      {step === 1 && (
        <div className="fixed inset-x-0 bottom-0 z-10 border-t border-[var(--app-border)] bg-[var(--app-bg)] px-4 py-3">
          <div className="mx-auto flex max-w-2xl items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-[var(--app-text)]">
                Выбрано: {selected.size} из {requests.length}
              </p>
              <p className="text-xs text-[var(--app-muted)]">
                Сумма: {formatRub(totalRub)} ₽
              </p>
            </div>
            <button
              type="button"
              onClick={toggleAll}
              disabled={requests.length === 0}
              className="rounded-full bg-[var(--app-input)] px-4 py-2 text-sm font-medium text-[var(--app-text)] transition hover:opacity-80 disabled:opacity-50"
            >
              {allSelected ? 'Сбросить' : 'Выбрать все'}
            </button>
          </div>
        </div>
      )}

      <TelegramMainButton
        isVisible={selected.size > 0}
        isLoading={isSubmitting}
        text={step === 1 ? 'Далее' : 'Сформировать поручение'}
        onClick={step === 1 ? handleNext : handleConfirm}
      />
    </div>
  )
}
